import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs, query, where, serverTimestamp, writeBatch, doc, getDoc, addDoc as firestoreAddDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { 
  MdCloudDownload, MdCloudUpload, MdShoppingCart, MdSync, MdWifiOff, 
  MdPerson, MdList, MdOutlineAccountBalanceWallet, MdHistory, MdShare, MdLogout 
} from "react-icons/md";
import BlankLayout from "../components/BlankLayout";

// Cache Keys for offline persistence
const CACHE_KEYS = {
  MASTER_DATA: "field_master_data_v3",
  VAN_STOCK: "field_van_stock_v2",         
  OUTLET_LEDGERS: "field_outlet_ledgers_v2", 
  PAST_BILLS: "field_past_bills",       
  BILL_QUEUE: "field_bill_queue",
  COLLECTION_QUEUE: "field_collection_queue",
  ACTIVE_PROFILE: "field_active_profile_v2"
};

const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FieldBilling() {
  const [userProfile, setUserProfile] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile"); 

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        window.location.href = "/";
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          setUserProfile(snap.data());
        }
      } catch (err) {
        console.error("Error loading user profile in FieldBilling:", err);
      }
    });
    return () => unsub();
  }, []);

  // Master Data State
  const [partyList, setPartyList] = useState([]);
  const [outletList, setOutletList] = useState([]);
  const [skuMaster, setSkuMaster] = useState({});
  const [pastBills, setPastBills] = useState({}); 
  
  // Field State
  const [partyId, setPartyId] = useState("");
  const [vanStock, setVanStock] = useState({ meta: { loadedOrderIds: [] }, items: {} });
  const [ledgers, setLedgers] = useState({}); 
  const [companyInfo, setCompanyInfo] = useState({ name: "M.D. SONS", gstin: "", address: "" });
  
  // Queues
  const [billQueue, setBillQueue] = useState([]);
  const [collectionQueue, setCollectionQueue] = useState([]);

  // Billing Tab
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [cart, setCart] = useState({});
  const [productSearch, setProductSearch] = useState("");
  const [lastBill, setLastBill] = useState(null);

  // Collections Tab
  const [collectionOutlet, setCollectionOutlet] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [onlineAmount, setOnlineAmount] = useState("");

  // UI State
  const [summaryType, setSummaryType] = useState("SUMMARY"); 
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [historySearch, setHistorySearch] = useState("");
  const [outletSearch, setOutletSearch] = useState("");
  const [userCoords, setUserCoords] = useState(null);
  const [isSortingNearby, setIsSortingNearby] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [regForm, setRegForm] = useState({ name: "", address: "", landmark: "", phone: "", limit: "" });
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [showPreview, setShowPreview] = useState(false);

  const toggleInvoice = (id) => {
     setExpandedInvoices(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // 0. CUSTOM PTR CALCULATION ENGINE (Strict GST/Margin compliant)
  const getSkuPTR = (skuCode) => {
     const m = skuMaster[skuCode];
     if (!m) return 0;
     const qf = Number(m.minQty || 1);
     const basic = Number(m.Basic_Eway || 0);
     const mrp = Number(m.mrp || 0);
     // Formula: ((Basic * qf) + (GST Split on MRP * qf)) + 0.8% salesman margin
     const pts = (basic * qf) + ((mrp - (mrp / 1.4)) * qf);
     const ptr = pts + (pts * 0.008);
     return ptr;
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const myOutlets = useMemo(() => outletList.filter(o => o.partyId === partyId), [outletList, partyId]);

  const sortedAndFilteredOutlets = useMemo(() => {
    let list = [...myOutlets].filter(o => 
      o.outletName.toLowerCase().includes(outletSearch.toLowerCase()) || 
      (o.landmark || "").toLowerCase().includes(outletSearch.toLowerCase())
    );

    if (isSortingNearby && userCoords) {
      list.sort((a, b) => {
        const dA = calculateDistance(userCoords.lat, userCoords.lng, a.location?.lat, a.location?.lng);
        const dB = calculateDistance(userCoords.lat, userCoords.lng, b.location?.lat, b.location?.lng);
        return dA - dB;
      });
    } else {
      list.sort((a, b) => a.outletName.localeCompare(b.outletName));
    }
    return list;
  }, [outletList, partyId, outletSearch, isSortingNearby, userCoords]);

  const toggleNearbySort = () => {
    if (isSortingNearby) {
      setIsSortingNearby(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setIsSortingNearby(true);
      },
      (err) => alert("GPS Error: " + err.message),
      { enableHighAccuracy: true }
    );
  };

  useEffect(() => {
    const loadData = () => {
      try {
        const _master = localStorage.getItem(CACHE_KEYS.MASTER_DATA);
        if (_master) {
          const m = JSON.parse(_master);
          setPartyList(m.parties || []);
          setOutletList(m.outlets || []);
          setSkuMaster(m.skus || {});
          if (m.company) setCompanyInfo(m.company);
        }
        
        const _profile = localStorage.getItem(CACHE_KEYS.ACTIVE_PROFILE);
        if (_profile) setPartyId(_profile);

        const _stock = localStorage.getItem(CACHE_KEYS.VAN_STOCK);
        if (_stock) setVanStock(JSON.parse(_stock));

        const _ledgers = localStorage.getItem(CACHE_KEYS.OUTLET_LEDGERS);
        if (_ledgers) setLedgers(JSON.parse(_ledgers));

        const _pastBills = localStorage.getItem(CACHE_KEYS.PAST_BILLS);
        if (_pastBills) setPastBills(JSON.parse(_pastBills));

        const _bq = localStorage.getItem(CACHE_KEYS.BILL_QUEUE);
        if (_bq) setBillQueue(JSON.parse(_bq));

        const _cq = localStorage.getItem(CACHE_KEYS.COLLECTION_QUEUE);
        if (_cq) setCollectionQueue(JSON.parse(_cq));
        
      } catch (err) { console.error("Local Load Error", err); }
    };
    loadData();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const downloadMorningData = async () => {
    if (!userProfile) return alert("User session loading, please wait...");
    if (!partyId) return alert("Select profile first.");
    if (!isOnline) return alert("Internet required for Morning Sync.");
    setDownloading(true);
    try {
      const skuSnap = await getDocs(collection(db, "skuMaster"));
      const skus = {};
      skuSnap.forEach(d => {
        const d_ = d.data();
        if (d_.sku) skus[d_.sku.trim().toUpperCase()] = d_;
      });

      const pType = userProfile.partytype || "hawker";
      const partySnap = await getDocs(
        query(
          collection(db, "partyMaster"),
          where("clientId", "==", userProfile.clientId),
          where("partytype", "==", pType)
        )
      );
      const parties = partySnap.docs.map(d => d.data());

      const outletSnap = await getDocs(query(collection(db, "DsOutletMaster"), where("partyId", "==", partyId), where("status", "==", "active")));
      const outlets = outletSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const companySnap = await getDoc(doc(db, "companyInfo", "default"));
      const company = companySnap.exists() ? companySnap.data() : { name: "M.D. SONS" };

      localStorage.setItem(CACHE_KEYS.MASTER_DATA, JSON.stringify({ parties, outlets, skus, company }));
      localStorage.setItem(CACHE_KEYS.ACTIVE_PROFILE, partyId);
      
      setSkuMaster(skus); setPartyList(parties); setOutletList(outlets); setCompanyInfo(company);

      const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const billsSnap = await getDocs(query(collection(db, "fieldMarketOrders"), where("partyId", "==", partyId), where("createdAt", ">=", sevenDaysAgo.toISOString())));
      const pBills = {};
      billsSnap.docs.forEach(d => {
         const b = d.data();
         if (!pBills[b.outletId]) pBills[b.outletId] = [];
         pBills[b.outletId].push(b);
      });
      setPastBills(pBills);
      localStorage.setItem(CACHE_KEYS.PAST_BILLS, JSON.stringify(pBills));

      const currentLocalLedgers = {};
      outlets.forEach(o => {
         currentLocalLedgers[o.id] = { opening: Number(o.fieldBalance || 0), billed: 0, collected: 0, yearlyLimit: Number(o.yearlyLimit || 0), yearlyUsed: Number(o.yearlyUsed || 0) };
      });
      setLedgers(currentLocalLedgers);
      localStorage.setItem(CACHE_KEYS.OUTLET_LEDGERS, JSON.stringify(currentLocalLedgers));

      alert("🎉 Morning Sync Complete!");
    } catch (err) { alert("Download failed: " + err.message); } finally { setDownloading(false); }
  };

  const pullVanStock = async () => {
    if (!partyId || !isOnline) return alert("Profile & Online Required.");
    setDownloading(true);
    try {
      const todayIST = new Date(new Date().getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
      const q = query(collection(db, "orders"), where("partyId", "==", partyId), where("approvedDateOnly", "==", todayIST), where("status", "==", "APPROVED"));
      const snap = await getDocs(q);
      const newStockState = { meta: { loadedOrderIds: [ ...(vanStock.meta?.loadedOrderIds || []) ] }, items: { ...vanStock.items } };
      let loadedAny = false;
      snap.docs.forEach(doc => {
         if (newStockState.meta.loadedOrderIds.includes(doc.id)) return;
         Object.entries(doc.data().items || {}).forEach(([sku, qty]) => {
            const qf = Number(skuMaster[sku]?.minQty || 1);
            const convertedPcs = Math.round(Number(qty) / qf);
            if (!newStockState.items[sku]) newStockState.items[sku] = { opening: 0, inward: 0, sales: 0, closing: 0 };
            newStockState.items[sku].inward += convertedPcs;
            newStockState.items[sku].closing = (newStockState.items[sku].opening + newStockState.items[sku].inward - newStockState.items[sku].sales);
         });
         newStockState.meta.loadedOrderIds.push(doc.id); loadedAny = true;
      });
      if (loadedAny) {
         setVanStock(newStockState);
         localStorage.setItem(CACHE_KEYS.VAN_STOCK, JSON.stringify(newStockState));
         alert("Stock Updated.");
      } else alert("Already loaded.");
    } catch (err) { alert(err.message); } finally { setDownloading(false); }
  };

  const adjustQty = (sku, delta) => {
    const available = Number(vanStock.items[sku]?.closing || 0);
    const current = Number(cart[sku] || 0);
    const next = Math.max(0, current + delta);
    if (next > available && delta > 0) return;
    setCart({ ...cart, [sku]: next === 0 ? "" : String(next) });
  };

  const generateBillOffline = () => {
    if (!selectedOutlet || cartTotals.tQty <= 0) return;
    const outletObj = outletList.find(o => o.id === selectedOutlet);
    const billId = `FBO-${Date.now().toString().slice(-6)}`;
    const linesArray = [];
    const updatedItems = { ...vanStock.items };

    Object.entries(cart).forEach(([sku, qty]) => {
      const q = Number(qty || 0);
      if (q > 0) {
          const m = skuMaster[sku];
          const ptr = getSkuPTR(sku);
          linesArray.push({ 
             sku, skuName: m?.sku || sku, qty: q, qf: Number(m?.minQty || 1), hsn: (m?.hsn || "").trim(),
             unitPtr: ptr, unitMrp: Number(m?.mrp || 0), unitBasic: Number(m?.Basic_Eway || 0), lineTotal: ptr * q 
          });
         updatedItems[sku].sales += q;
         updatedItems[sku].closing = (updatedItems[sku].opening + updatedItems[sku].inward - updatedItems[sku].sales);
      }
    });
    
    const billDoc = { id: billId, partyId, outletId: selectedOutlet, outletName: outletObj.outletName, lines: linesArray, totalQty: cartTotals.tQty, totalAmount: cartTotals.tAmt, createdAt: new Date().toISOString() };
    setVanStock({ ...vanStock, items: updatedItems });
    localStorage.setItem(CACHE_KEYS.VAN_STOCK, JSON.stringify({ ...vanStock, items: updatedItems }));

    const nl = { ...ledgers };
    if (!nl[selectedOutlet]) nl[selectedOutlet] = { opening: 0, billed: 0, collected: 0 };
    nl[selectedOutlet].billed += cartTotals.tAmt;
    setLedgers(nl);
    localStorage.setItem(CACHE_KEYS.OUTLET_LEDGERS, JSON.stringify(nl));
    
    setBillQueue([...billQueue, billDoc]);
    localStorage.setItem(CACHE_KEYS.BILL_QUEUE, JSON.stringify([...billQueue, billDoc]));
    setCart({});
    
    if (window.confirm("Bill Saved! Print Receipt?")) handleThermalPrint(billDoc);
    else shareToWhatsapp(billDoc);
  };

  const handleThermalPrint = (bill) => {
    const s = ledgers[bill.outletId] || { opening: 0, billed: 0, collected: 0 };
    const bal = s.opening + s.billed - s.collected;
    
    const html = `
      <html>
        <head><style>
          @page { margin: 0; } body { font-family: 'Courier New', monospace; width: 58mm; padding: 5mm; margin: 0; font-size: 11px; }
          .center { text-align: center; } .bold { font-weight: bold; } .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; } th { text-align: left; border-bottom: 1px solid #000; } .right { text-align: right; }
        </style></head>
        <body>
          <div class="center bold" style="font-size:14px">${companyInfo.name}</div>
          <div class="center">${companyInfo.gstin ? "GSTIN: "+companyInfo.gstin : ""}</div>
          <div class="divider"></div>
          <div>B.No: ${bill.id} | ${new Date(bill.createdAt).toLocaleDateString()}</div>
          <div class="bold">OUTLET: ${bill.outletName}</div>
          <div class="divider"></div>
          <table>
            <thead><tr><th>ITEM</th><th class="right">QTY</th><th class="right">AMT</th></tr></thead>
            <tbody>
              ${[...bill.lines].sort((a,b)=>(b.hsn||"").localeCompare(a.hsn||"") || a.skuName.localeCompare(b.skuName)).map(l=>`
                <tr><td>${l.skuName}<br/><small>HSN: ${l.hsn||"N/A"}</small></td><td class="right">${l.qty}</td><td class="right">${l.lineTotal.toFixed(2)}</td></tr>
              `).join('')}
            </tbody>
          </table>
          <div class="divider"></div>
          ${(() => {
            let tBasic = 0, tGst = 0, tGrand = 0;
            bill.lines.forEach(l => {
              const q = l.qty, qf = l.qf, uMrp = l.unitMrp, uBasic = l.unitBasic;
              const lineGst = (uMrp - (uMrp / 1.4)) * qf;
              const linePts = (uBasic * qf) + lineGst;
              const lineMargin = linePts * 0.008;
              tBasic += ((uBasic * qf) + lineMargin) * q;
              tGst += lineGst * q;
              tGrand += (linePts + lineMargin) * q;
            });
            return `<div class="right">BASIC: ${tBasic.toFixed(2)}</div>
                    <div class="right">GST: ${tGst.toFixed(2)}</div>
                    <div class="right bold">TOTAL: ${tGrand.toFixed(2)}</div>`;
          })()}
          <div class="right">Total Due: ${bal.toFixed(2)}</div>
          <div class="divider"></div>
          <div class="center">THANK YOU</div>
          <script>window.onload=()=>{ window.print(); setTimeout(()=>window.close(), 500); };</script>
        </body>
      </html>
    `;
    const w = window.open('', '_blank', 'width=300'); w.document.write(html); w.document.close();
  };

  const shareToWhatsapp = (bill) => {
     let txt = `*${companyInfo.name}*\nBill: ${bill.id}\nOutlet: ${bill.outletName}\n\n`;
     bill.lines.forEach(l => txt += `- ${l.skuName} x ${l.qty} = ₹${l.lineTotal.toFixed(2)}\n`);
     txt += `\n*Today:* ₹${bill.totalAmount.toFixed(2)}`;
     const ph = outletList.find(o => o.id === bill.outletId)?.phone || "";
     window.open(`https://wa.me/91${ph}?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const recordCollection = () => {
     const t = (Number(cashAmount) || 0) + (Number(onlineAmount) || 0);
     if (t <= 0 || !collectionOutlet) return;
     const doc = { id: `COL-${Date.now()}`, partyId, outletId: collectionOutlet, cashCollected: Number(cashAmount), onlineCollected: Number(onlineAmount), totalCollected: t, createdAt: new Date().toISOString() };
     const nl = { ...ledgers }; nl[collectionOutlet].collected += t;
     setLedgers(nl); localStorage.setItem(CACHE_KEYS.OUTLET_LEDGERS, JSON.stringify(nl));
     setCollectionQueue([...collectionQueue, doc]); localStorage.setItem(CACHE_KEYS.COLLECTION_QUEUE, JSON.stringify([...collectionQueue, doc]));
     setCashAmount(""); setOnlineAmount(""); alert("Payment Recorded.");
  };

  const syncToCloud = async (isManual = false) => {
    if (!isOnline) return;
    if (syncing) return;
    setSyncing(true);
    try {
       const batch = writeBatch(db);
       const bQueue = [...billQueue];
       const cQueue = [...collectionQueue];
       if (bQueue.length === 0 && cQueue.length === 0) {
         setSyncing(false);
         return;
       }
       bQueue.forEach(b => batch.set(doc(collection(db, "fieldMarketOrders")), { ...b, cloudSyncedAt: serverTimestamp() }));
       cQueue.forEach(c => batch.set(doc(collection(db, "fieldMarketCollections")), { ...c, cloudSyncedAt: serverTimestamp() }));
       Object.entries(ledgers).forEach(([oid, s]) => {
          const bal = (s.opening || 0) + (s.billed || 0) - (s.collected || 0);
          batch.update(doc(db, "DsOutletMaster", oid), { fieldBalance: bal, yearlyUsed: (s.yearlyUsed + s.billed), lastSync: serverTimestamp() });
       });
       await batch.commit();
       setBillQueue([]); setCollectionQueue([]);
       localStorage.removeItem(CACHE_KEYS.BILL_QUEUE); localStorage.removeItem(CACHE_KEYS.COLLECTION_QUEUE);
       if (isManual) {
         alert("Sync Success!");
       } else {
         console.log("Automatic Sync Success!");
       }
    } catch (err) { 
       if (isManual) {
         alert(err.message); 
       } else {
         console.error("Auto Sync Error: ", err.message);
       }
    } finally { setSyncing(false); }
  };

  useEffect(() => {
    if (isOnline && (billQueue.length > 0 || collectionQueue.length > 0)) {
      syncToCloud(false);
    }
  }, [isOnline, billQueue.length, collectionQueue.length]);

  // Memoized Helpers
  const activeSkus = useMemo(() => Object.keys(vanStock.items).sort(), [vanStock]);
  const cartTotals = useMemo(() => {
    let q = 0, a = 0;
    Object.entries(cart).forEach(([sku, qty]) => { const n = Number(qty) || 0; if(n>0){ q += n; a += (n * getSkuPTR(sku)); } });
    return { tQty: q, tAmt: a };
  }, [cart, skuMaster]);

  // Tab Icon Helper
  const TabIcon = ({ icon, label, id, badge }) => (
     <div onClick={()=>setActiveTab(id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", color: activeTab === id ? "#3b82f6" : "#94a3b8", fontSize: "10px", fontWeight: "bold", cursor: "pointer", position: "relative" }}>
        {icon} <div style={{ marginTop: "2px" }}>{label}</div>
        {badge && <div style={{ position: "absolute", top: "0", right: "20%", background: "red", width: "8px", height: "8px", borderRadius: "50%" }} />}
     </div>
  );

  return (
    <BlankLayout>
      <div style={{ height: "100vh", background: "#f8fafc", fontFamily: "Inter, sans-serif", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ background: isOnline ? "#2563eb" : "#475569", color: "#fff", padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
           <div><h2 style={{ margin: 0, fontSize: "18px" }}>Field Billing v2.5</h2><div style={{ fontSize: "11px" }}>{partyId || "OFFLINE"}</div></div>
           <button onClick={()=>window.location.href='/base'} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "6px 12px", borderRadius: "8px" }}>Leave</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
           {showPreview && (
             <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
                <div style={{ background: "#fff", width: "100%", maxWidth: "500px", borderRadius: "24px", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                   <div style={{ padding: "24px", borderBottom: "1px solid #eee" }}>
                      <h2 style={{ margin: 0, fontSize: "18px" }}>Review Order</h2>
                      <div style={{ fontSize: "12px", color: "#666" }}>{outletList.find(o => o.id === selectedOutlet)?.outletName}</div>
                   </div>
                   <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
                      {Object.entries(cart).filter(([_, q]) => Number(q) > 0).map(([sku, q]) => (
                         <div key={sku} style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px solid #f9f9f9", paddingBottom: "8px" }}>
                            <div>
                               <div style={{ fontWeight: "bold", fontSize: "14px" }}>{skuMaster[sku]?.sku}</div>
                               <div style={{ fontSize: "11px", color: "#999" }}>{q} units @ {formatCurrency(getSkuPTR(sku))}</div>
                            </div>
                            <div style={{ fontWeight: "bold" }}>{formatCurrency(Number(q) * getSkuPTR(sku))}</div>
                         </div>
                      ))}
                   </div>
                   <div style={{ padding: "20px", background: "#f8fafc", borderTop: "1px solid #eee" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "20px", fontWeight: "bold", marginBottom: "20px" }}>
                         <span>Total</span>
                         <span>{formatCurrency(cartTotals.tAmt)}</span>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                         <button onClick={()=>setShowPreview(false)} style={{ flex: 1, padding: "16px", borderRadius: "12px", border: "1px solid #ddd", background: "#fff", fontWeight: "bold" }}>Edit</button>
                         <button onClick={()=>{ setShowPreview(false); generateBillOffline(); }} style={{ flex: 1, padding: "16px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold" }}>Confirm</button>
                      </div>
                   </div>
                </div>
             </div>
           )}

           {activeTab === "profile" && (
              <div style={{ padding: "16px" }}>
                 <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #eee", marginBottom: "20px" }}>
                    <select value={partyId} disabled={!!partyId} onChange={e=>setPartyId(e.target.value)} style={{ width: "100%", padding: "16px", borderRadius: "12px", border: "1px solid #ddd", fontWeight: "bold" }}>
                       <option value="">-- SELECT PROFILE --</option>
                       {partyList.map(p => <option key={p.partyId} value={p.partyId}>{p.name} ({p.partyId})</option>)}
                    </select>
                    {partyId && (
                       <div style={{ marginTop: "20px", display: "grid", gap: "12px" }}>
                          <button onClick={downloadMorningData} disabled={downloading} style={{ padding: "16px", borderRadius: "12px", border: "1px solid #ddd", background: "#f8fafc", fontWeight: "bold" }}>Morning Sync (config)</button>
                          <button onClick={pullVanStock} disabled={downloading} style={{ padding: "16px", borderRadius: "12px", border: "none", background: "#22c55e", color: "#fff", fontWeight: "bold" }}>Inward Today's Stock</button>
                          <button onClick={()=>setActiveTab("register")} style={{ padding: "16px", borderRadius: "12px", border: "1px solid #3b82f6", background: "#eff6ff", color: "#3b82f6", fontWeight: "bold" }}>+ New Outlet Registration</button>
                       </div>
                    )}
                 </div>
                 {(billQueue.length > 0 || collectionQueue.length > 0) && (
                    <button onClick={() => syncToCloud(true)} disabled={syncing} style={{ width: "100%", padding: "20px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "16px", fontWeight: "bold", fontSize: "16px" }}>🚀 SYNC PENDING DATA ({billQueue.length + collectionQueue.length})</button>
                 )}
                 {partyId && <button onClick={()=>{ if(window.confirm("Format App?")){ localStorage.clear(); window.location.reload(); }}} style={{ width: "100%", marginTop: "40px", color: "#94a3b8", background: "none", border: "none", fontSize: "10px" }}>Factory Reset Device</button>}
              </div>
           )}

           {activeTab === "register" && (
              <div style={{ padding: "16px" }}>
                 <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "16px" }}>
                    <button onClick={()=>setActiveTab("profile")} style={{ padding: "12px", borderRadius: "10px", border: "none", background: "#eee" }}>←</button>
                    <h2 style={{ margin: 0, fontSize: "18px" }}>New Outlet</h2>
                 </div>
                 <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #eee" }}>
                    {["name", "landmark", "phone", "limit", "address"].map(f => (
                       <div key={f} style={{ marginBottom: "15px" }}>
                          <label style={{ display: "block", fontSize: "11px", fontWeight: "bold", color: "#64748b", textTransform: "uppercase" }}>{f}</label>
                          {f === "address" ? 
                            <textarea value={regForm[f]} onChange={e=>setRegForm({...regForm, [f]: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #ddd" }} /> :
                            <input type={f === "limit" ? "number" : "text"} value={regForm[f]} onChange={e=>setRegForm({...regForm, [f]: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #ddd" }} />
                          }
                       </div>
                    ))}
                    <button onClick={async ()=>{
                       setRegistering(true);
                       navigator.geolocation.getCurrentPosition(async pos => {
                          const payload = { partyId, outletName: regForm.name, landmark: regForm.landmark, address: regForm.address, phone: regForm.phone, yearlyLimit: Number(regForm.limit), location: { lat: pos.coords.latitude, lng: pos.coords.longitude }, status: "active", createdAt: new Date().toISOString() };
                          const ref = await firestoreAddDoc(collection(db, "DsOutletMaster"), payload);
                          setOutletList([...outletList, { id: ref.id, ...payload }]);
                          setActiveTab("billing"); setRegistering(false); alert("Success!");
                       }, err => { alert("GPS REQ: "+err.message); setRegistering(false); });
                    }} disabled={registering} style={{ width: "100%", padding: "16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "bold" }}>
                       {registering ? "Capturing GPS..." : "Save Outlet"}
                    </button>
                 </div>
              </div>
           )}

           {activeTab === "billing" && (
              <div style={{ padding: "16px", paddingBottom: "140px" }}>
                 {!selectedOutlet ? (
                    <>
                       <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
                          <input type="text" placeholder="Search outlet..." value={outletSearch} onChange={e=>setOutletSearch(e.target.value)} style={{ flex: 1, padding: "14px", borderRadius: "12px", border: "1px solid #ddd" }} />
                          <button onClick={toggleNearbySort} style={{ padding: "14px", borderRadius: "12px", border: "none", background: isSortingNearby ? "#3b82f6" : "#f1f5f9", color: isSortingNearby ? "#fff" : "#64748b" }}>
                             {isSortingNearby ? "📍 Nearby" : "🗺️ GPS"}
                          </button>
                       </div>
                       <div style={{ display: "grid", gap: "12px" }}>
                          {sortedAndFilteredOutlets.map(o => {
                             const l = ledgers[o.id] || { opening: 0, billed: 0, collected: 0 };
                             const bal = l.opening + l.billed - l.collected;
                             const dist = (isSortingNearby && userCoords) ? calculateDistance(userCoords.lat, userCoords.lng, o.location?.lat, o.location?.lng) : null;
                             return (
                                <div key={o.id} onClick={() => setSelectedOutlet(o.id)} style={{ background: "#fff", padding: "16px", borderRadius: "16px", border: "1px solid #eee", cursor: "pointer" }}>
                                   <div style={{ display: "flex", justifyContent: "space-between" }}>
                                      <div>
                                         <div style={{ fontWeight: "700", color: "#1e293b" }}>{o.outletName}</div>
                                         <div style={{ fontSize: "11px", color: "#64748b" }}>{o.landmark || "No Landmark"}</div>
                                      </div>
                                      <div style={{ textAlign: "right" }}>
                                         <div style={{ fontWeight: "bold", color: bal > 0 ? "#ef4444" : "#22c55e" }}>{formatCurrency(bal)}</div>
                                         {dist !== null && <div style={{ fontSize: "10px", color: "#3b82f6" }}>{dist < 1 ? Math.round(dist*1000)+"m" : dist.toFixed(1)+"km"}</div>}
                                      </div>
                                   </div>
                                </div>
                             )
                          })}
                       </div>
                    </>
                 ) : (
                    <>
                       <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", background: "#fff", padding: "12px", borderRadius: "12px", border: "1px solid #eee" }}>
                          <div>
                             <div style={{ fontSize: "10px", color: "#64748b" }}>BILLING:</div>
                             <div style={{ fontWeight: "bold" }}>{outletList.find(o => o.id === selectedOutlet)?.outletName}</div>
                          </div>
                          <button onClick={()=>{ setSelectedOutlet(""); setCart({}); }} style={{ padding: "8px 12px", borderRadius: "8px", border: "none", background: "#fee2e2", color: "red", fontWeight: "bold", fontSize: "11px" }}>Change</button>
                       </div>
                       
                       <input type="text" placeholder="Search product..." value={productSearch} onChange={e=>setProductSearch(e.target.value)} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #ddd", marginBottom: "16px" }} />
                       {activeSkus.filter(s => (skuMaster[s]?.sku || s).toLowerCase().includes(productSearch.toLowerCase())).map(sku => {
                          const itm = vanStock.items[sku] || { closing: 0 };
                          const qty = Number(cart[sku] || 0);
                          return (
                             <div key={sku} style={{ background: "#fff", padding: "16px", borderRadius: "12px", border: "1px solid #eee", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ flex: 1 }}>
                                   <div style={{ fontWeight: "bold" }}>{skuMaster[sku]?.sku}</div>
                                   <div style={{ fontSize: "10px", color: "#666" }}>Stock: {itm.closing} | PTR: {formatCurrency(getSkuPTR(sku))}</div>
                                </div>
                                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                   <button onClick={()=>adjustQty(sku, -1)} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#f1f5f9" }}>-</button>
                                   <div style={{ width: 20, textAlign: "center", fontWeight: "bold" }}>{qty}</div>
                                   <button onClick={()=>adjustQty(sku, 1)} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff" }}>+</button>
                                </div>
                             </div>
                          )
                       })}

                       <div style={{ position: "fixed", bottom: "85px", left: "16px", right: "16px", background: "#fff", padding: "16px", borderRadius: "20px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid #eee" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontWeight: "900", fontSize: "18px" }}>
                             <span>Items: {cartTotals.tQty}</span>
                             <span>{formatCurrency(cartTotals.tAmt)}</span>
                          </div>
                          <button onClick={()=>setShowPreview(true)} disabled={cartTotals.tQty === 0} style={{ width: "100%", padding: "16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "bold", fontSize: "16px" }}>GENERATE BILL</button>
                       </div>
                    </>
                 )}
              </div>
           )}

           {activeTab === "collection" && (
              <div style={{ padding: "16px" }}>
                 {!collectionOutlet ? (
                    <>
                       <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
                          <input type="text" placeholder="Search for collection..." value={outletSearch} onChange={e=>setOutletSearch(e.target.value)} style={{ flex: 1, padding: "14px", borderRadius: "12px", border: "1px solid #ddd" }} />
                          <button onClick={toggleNearbySort} style={{ padding: "14px", borderRadius: "12px", border: "none", background: isSortingNearby ? "#3b82f6" : "#f1f5f9", color: isSortingNearby ? "#fff" : "#64748b" }}>
                             <MdSync size={20} />
                          </button>
                       </div>
                       {myOutlets.map(o => {
                          const s = ledgers[o.id] || { opening: 0, billed: 0, collected: 0 };
                          const bal = s.opening + s.billed - s.collected;
                          return (
                             <div key={o.id} onClick={() => setCollectionOutlet(o.id)} style={{ background: "#fff", padding: "16px", borderRadius: "12px", border: "1px solid #eee", marginBottom: "10px", cursor: "pointer" }}>
                                <div style={{ fontWeight: "bold" }}>{o.outletName}</div>
                                <div style={{ fontSize: "11px", color: bal > 0 ? "#ef4444" : "#22c55e" }}>Balance: {formatCurrency(bal)}</div>
                             </div>
                          )
                       })}
                    </>
                 ) : (
                    <div style={{ background: "#fff", padding: "24px", borderRadius: "20px", border: "1px solid #eee" }}>
                       <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
                          <div>
                             <div style={{ fontSize: "10px", fontWeight: "bold" }}>COLLECTING FROM:</div>
                             <div style={{ fontSize: "18px", fontWeight: "900" }}>{outletList.find(o => o.id === collectionOutlet)?.outletName}</div>
                          </div>
                          <button onClick={()=>setCollectionOutlet("")} style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#fee2e2", color: "red", fontWeight: "bold" }}>Change</button>
                       </div>
                       <input type="number" placeholder="CASH AMOUNT" value={cashAmount} onChange={e=>setCashAmount(e.target.value)} style={{ width: "100%", padding: "18px", border: "1px solid #ddd", borderRadius: "12px", fontSize: "20px", fontWeight: "bold", marginBottom: "12px" }} />
                       <input type="number" placeholder="ONLINE AMOUNT" value={onlineAmount} onChange={e=>setOnlineAmount(e.target.value)} style={{ width: "100%", padding: "18px", border: "1px solid #ddd", borderRadius: "12px", fontSize: "20px", fontWeight: "bold", marginBottom: "20px" }} />
                       <button onClick={recordCollection} style={{ width: "100%", padding: "20px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "bold", fontSize: "18px" }}>🚀 Save Payment</button>
                    </div>
                 )}
              </div>
           )}

           {activeTab === "history" && (
              <div style={{ padding: "16px", paddingBottom: "100px" }}>
                 <input type="text" placeholder="Search history..." value={historySearch} onChange={e=>setHistorySearch(e.target.value)} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #ddd", marginBottom: "16px" }} />
                 
                 <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
                    <div style={{ flex: 1 }}>
                       <label style={{ display: "block", fontSize: "10px", fontWeight: "bold", color: "#64748b", marginBottom: "4px" }}>FROM DATE</label>
                       <input 
                          type="date" 
                          value={startDate} 
                          onChange={e => setStartDate(e.target.value)} 
                          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "13px" }} 
                       />
                    </div>
                    <div style={{ flex: 1 }}>
                       <label style={{ display: "block", fontSize: "10px", fontWeight: "bold", color: "#64748b", marginBottom: "4px" }}>TO DATE</label>
                       <input 
                          type="date" 
                          value={endDate} 
                          onChange={e => setEndDate(e.target.value)} 
                          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "13px" }} 
                       />
                    </div>
                 </div>

                 {Object.values(pastBills).flat().filter(b => {
                    const bDate = b.createdAt.slice(0, 10);
                    const matchesSearch = b.outletName.toLowerCase().includes(historySearch.toLowerCase()) || b.id.toLowerCase().includes(historySearch.toLowerCase());
                    const matchesDate = bDate >= startDate && bDate <= endDate;
                    return matchesSearch && matchesDate;
                 }).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).map(b => (
                    <div key={b.id} style={{ background: "#fff", padding: "16px", borderRadius: "16px", border: "1px solid #eee", marginBottom: "12px" }}>
                       <div onClick={()=>toggleInvoice(b.id)} style={{ display: "flex", justifyContent: "space-between", cursor: "pointer", fontWeight: "bold" }}>
                          <span>{b.outletName}</span>
                          <span>{formatCurrency(b.totalAmount)}</span>
                       </div>
                       <div style={{ fontSize: "10px", color: "#999", marginTop: "4px" }}>{b.id} | {new Date(b.createdAt).toLocaleString()}</div>
                       {expandedInvoices[b.id] && (
                          <div style={{ marginTop: "12px", padding: "10px", background: "#f8fafc", borderRadius: "10px", fontSize: "11px" }}>
                             {b.lines.map((l, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                   <span>{l.skuName} x {l.qty}</span>
                                   <span>{formatCurrency(l.lineTotal)}</span>
                                </div>
                             ))}
                          </div>
                       )}
                       <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                          <button onClick={()=>handleThermalPrint(b)} style={{ flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #3b82f6", color: "#3b82f6", background: "#eff6ff", fontWeight: "bold" }}>Print</button>
                          <button onClick={()=>shareToWhatsapp(b)} style={{ flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #22c55e", color: "#22c55e", background: "#f0fdf4", fontWeight: "bold" }}>WA</button>
                       </div>
                    </div>
                 ))}
              </div>
           )}

           {activeTab === "summary" && (
              <div style={{ padding: "16px" }}>
                 <div style={{ display: "flex", background: "#eee", padding: "4px", borderRadius: "10px" }}>
                    <button onClick={()=>setSummaryType("SUMMARY")} style={{ flex: 1, padding: "8px", border: "none", borderRadius: "8px", background: summaryType==="SUMMARY"?"#fff":"none", fontWeight:"bold" }}>Ledgers</button>
                    <button onClick={()=>setSummaryType("STOCK")} style={{ flex: 1, padding: "8px", border: "none", borderRadius: "8px", background: summaryType==="STOCK"?"#fff":"none", fontWeight:"bold" }}>Van Stock</button>
                 </div>
                 {summaryType === "SUMMARY" ? (
                    <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #eee", marginTop: "16px", overflow: "hidden" }}>
                       <table width="100%" style={{ fontSize: "11px", borderCollapse: "collapse" }}>
                          <thead style={{ background: "#f8fafc" }}><tr><th align="left" style={{ padding: "10px" }}>OUTLET</th><th align="right">OP</th><th align="right">SALE</th><th align="right">CL</th></tr></thead>
                          <tbody>
                             {myOutlets.map(o => {
                                const s = ledgers[o.id] || { opening: 0, billed: 0, collected: 0 };
                                return <tr key={o.id} style={{ borderBottom: "1px solid #eee" }}><td style={{ padding: "10px" }}>{o.outletName}</td><td align="right">{s.opening.toFixed(0)}</td><td align="right">{s.billed.toFixed(0)}</td><td align="right" style={{ fontWeight: "bold" }}>{(s.opening+s.billed-s.collected).toFixed(0)}</td></tr>
                             })}
                          </tbody>
                       </table>
                    </div>
                 ) : (
                    <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #eee", marginTop: "16px", overflow: "hidden" }}>
                       <table width="100%" style={{ fontSize: "11px", borderCollapse: "collapse" }}>
                          <thead style={{ background: "#f8fafc" }}><tr><th align="left" style={{ padding: "10px" }}>SKU</th><th align="right">IN</th><th align="right">SALE</th><th align="right">CL</th></tr></thead>
                          <tbody>
                             {activeSkus.map(sku => {
                                const i = vanStock.items[sku] || { inward: 0, sales: 0, closing: 0 };
                                return <tr key={sku} style={{ borderBottom: "1px solid #eee" }}><td style={{ padding: "10px" }}>{skuMaster[sku]?.sku}</td><td align="right">{i.inward}</td><td align="right">{i.sales}</td><td align="right" style={{ fontWeight: "bold" }}>{i.closing}</td></tr>
                             })}
                          </tbody>
                       </table>
                    </div>
                 )}
              </div>
           )}
        </div>

        {/* Bottom Nav */}
        <div style={{ background: "#fff", borderTop: "1px solid #e2e8f0", display: "flex", padding: "12px 0", paddingBottom: "30px" }}>
           <TabIcon id="profile" icon={<MdPerson size={24}/>} label="Profile" />
           <TabIcon id="billing" icon={<MdShoppingCart size={24}/>} label="Billing" />
           <TabIcon id="collection" icon={<MdOutlineAccountBalanceWallet size={24}/>} label="Collection" />
           <TabIcon id="history" icon={<MdHistory size={24}/>} label="History" />
           <TabIcon id="summary" icon={<MdList size={24}/>} label="Summary" badge={billQueue.length + collectionQueue.length > 0} />
        </div>
      </div>
    </BlankLayout>
  );
}
